// Deterministic 2D noise toolkit for the open-battlefield generator (CPU side).
// Mirrors the planet-surface shader's recipes (value-fbm + domain warp + worley) in 2D so the
// battlefield reads as the same "world family" as the scouted globe. Zero RNG state: every
// function is a pure hash of (x, z, seed) — stable across queries, frames, and rebuilds.

/** Integer-lattice hash → [0,1). Fast, deterministic, good enough for terrain. */
export function hash2(x: number, z: number, seed: number): number {
  let h = (x | 0) * 374761393 + (z | 0) * 668265263 + (seed | 0) * 974711;
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

/** Smooth value noise on the unit lattice, ~[0,1]. */
export function vnoize(x: number, z: number, seed: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  const xf = x - xi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = zf * zf * (3 - 2 * zf);
  const a = hash2(xi, zi, seed), b = hash2(xi + 1, zi, seed);
  const c = hash2(xi, zi + 1, seed), d = hash2(xi + 1, zi + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

/** fbm over `oct` octaves, ~[0,1] (centered near 0.5). */
export function fbm(x: number, z: number, seed: number, oct: number): number {
  let s = 0, amp = 0.5, f = 1, norm = 0;
  for (let i = 0; i < oct; i++) {
    s += amp * vnoize(x * f, z * f, seed + i * 131);
    norm += amp;
    f *= 2.03;
    amp *= 0.5;
  }
  return s / norm;
}

/** Domain-warped fbm (the "organic, not blobby-noise" recipe from the planet surface), ~[0,1]. */
export function warpFbm(x: number, z: number, seed: number, oct: number, warp: number): number {
  const wx = fbm(x + 5.2, z + 1.3, seed + 977, 3) - 0.5;
  const wz = fbm(x + 2.9, z + 8.4, seed + 461, 3) - 0.5;
  return fbm(x + wx * warp, z + wz * warp, seed, oct);
}

/** Ridged fbm: sharp crests, ~[0,1] (1 at ridge lines). */
export function ridged(x: number, z: number, seed: number, oct: number): number {
  return 1 - Math.abs(fbm(x, z, seed, oct) * 2 - 1);
}

/** 2D Worley/cellular noise (3×3 cells): distance to the nearest feature point, ~[0,1]. */
export function worley2(x: number, z: number, seed: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  let best = 8;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cx = xi + dx, cz = zi + dz;
      const px = cx + hash2(cx, cz, seed + 7);
      const pz = cz + hash2(cx, cz, seed + 31);
      const ddx = px - x, ddz = pz - z;
      const d = ddx * ddx + ddz * ddz;
      if (d < best) best = d;
    }
  }
  return Math.min(1, Math.sqrt(best));
}

/** Worley EDGE strength: distance between the two nearest feature points' distances (F2−F1).
 *  ≈0 on the borders between cells → thin connected crack/channel lines. ~[0,1]. */
export function worleyEdge2(x: number, z: number, seed: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  let f1 = 8, f2 = 8;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cx = xi + dx, cz = zi + dz;
      const px = cx + hash2(cx, cz, seed + 7);
      const pz = cz + hash2(cx, cz, seed + 31);
      const ddx = px - x, ddz = pz - z;
      const d = ddx * ddx + ddz * ddz;
      if (d < f1) { f2 = f1; f1 = d; } else if (d < f2) f2 = d;
    }
  }
  return Math.min(1, Math.sqrt(f2) - Math.sqrt(f1));
}

export const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
export function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
